/**
 * Procedural menu music — minor-key synth loop in 4 bars at ~95 BPM.
 * Bass + sustained pad + simple lead riff. No audio assets.
 *
 * Requires a user gesture before start() (handled by Play button context — but
 * the menu must start music *before* a click. We try to resume immediately and
 * also attach a one-shot user-gesture resume in case the browser blocks autoplay.)
 */
class MenuMusicImpl {
  private ctx?: AudioContext;
  private master?: GainNode;
  private timer?: number;
  private running = false;
  private barCount = 0;

  // A natural minor — Am, F, C, G
  private chords = [
    { root: 'A2', notes: ['A3', 'C4', 'E4'] }, // Am
    { root: 'F2', notes: ['F3', 'A3', 'C4'] }, // F
    { root: 'C2', notes: ['C3', 'E3', 'G3'] }, // C
    { root: 'G2', notes: ['G3', 'B3', 'D4'] }  // G
  ];

  // Lead riff: 8 eighth notes per bar (A minor pentatonic-ish)
  private lead: (string | null)[][] = [
    ['A4', null, 'C5', 'E5', null, 'C5', 'D5', 'C5'],
    ['A4', null, 'F4', 'A4', 'C5', null, 'A4', 'F4'],
    ['G4', null, 'E4', 'G4', 'C5', null, 'G4', 'E4'],
    ['B4', null, 'D5', 'B4', 'G4', null, 'B4', 'D5']
  ];

  start() {
    if (this.running) return;
    if (typeof window === 'undefined' || !window.AudioContext) return;
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    this.ctx = ctx;
    if (ctx.state === 'suspended') {
      void ctx.resume();
      // Fallback: resume on first user gesture if still suspended.
      const resume = () => { void ctx.resume(); window.removeEventListener('pointerdown', resume); window.removeEventListener('keydown', resume); };
      window.addEventListener('pointerdown', resume, { once: true });
      window.addEventListener('keydown', resume, { once: true });
    }

    const master = ctx.createGain();
    master.gain.value = 0.20;
    // Soft hi-cut so it doesn't sound harsh
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 4500;
    master.connect(lp).connect(ctx.destination);
    this.master = master;

    this.running = true;
    this.barCount = 0;
    this.scheduleBar();
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    if (this.timer != null) clearTimeout(this.timer);
    this.timer = undefined;
    void this.ctx?.close();
    this.ctx = undefined;
    this.master = undefined;
  }

  setVolume(v: number) {
    if (this.master) this.master.gain.value = Math.max(0, Math.min(1, v));
  }

  private scheduleBar() {
    if (!this.running || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const master = this.master;
    const bpm = 95;
    const secPerBeat = 60 / bpm;
    const barSec = secPerBeat * 4;
    const t0 = ctx.currentTime + 0.05;
    const idx = this.barCount % this.chords.length;
    const chord = this.chords[idx];
    const lead = this.lead[idx];

    // Bass — root note for the bar (held)
    this.playNote(master, this.freq(chord.root), t0, barSec * 0.95, {
      type: 'sawtooth', vol: 0.18, attack: 0.01, release: 0.5,
      lpFreq: 600
    });
    // Mid bass kick on beat 3
    this.playNote(master, this.freq(chord.root), t0 + secPerBeat * 2, secPerBeat * 0.9, {
      type: 'sawtooth', vol: 0.14, attack: 0.01, release: 0.3, lpFreq: 700
    });

    // Pad — chord triad sustained, soft sine
    for (const n of chord.notes) {
      this.playNote(master, this.freq(n), t0, barSec * 0.95, {
        type: 'sine', vol: 0.05, attack: 0.4, release: 0.6
      });
    }

    // Lead — 8 eighths
    for (let i = 0; i < lead.length; i++) {
      const n = lead[i];
      if (!n) continue;
      const start = t0 + i * (secPerBeat / 2);
      this.playNote(master, this.freq(n), start, secPerBeat * 0.45, {
        type: 'square', vol: 0.07, attack: 0.005, release: 0.12,
        lpFreq: 2400
      });
    }

    this.barCount++;
    this.timer = window.setTimeout(() => this.scheduleBar(), barSec * 1000);
  }

  private playNote(
    dest: AudioNode,
    freq: number,
    start: number,
    duration: number,
    opts: {
      type: OscillatorType;
      vol: number;
      attack: number;
      release: number;
      lpFreq?: number;
    }
  ) {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = opts.type;
    o.frequency.value = freq;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(opts.vol, start + opts.attack);
    g.gain.setValueAtTime(opts.vol, start + duration - opts.release);
    g.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    let last: AudioNode = o;
    if (opts.lpFreq) {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = opts.lpFreq;
      o.connect(lp);
      last = lp;
    }
    last.connect(g).connect(dest);
    o.start(start);
    o.stop(start + duration + 0.05);
  }

  // Note name → frequency. Supports A0..G9 with #/b.
  private freq(note: string): number {
    const m = /^([A-G])([#b]?)(\d)$/.exec(note);
    if (!m) return 440;
    const semis = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 } as Record<string, number>;
    let n = semis[m[1]];
    if (m[2] === '#') n += 1;
    else if (m[2] === 'b') n -= 1;
    const oct = parseInt(m[3], 10);
    const midi = (oct + 1) * 12 + n;
    return 440 * Math.pow(2, (midi - 69) / 12);
  }
}

export const MenuMusic = new MenuMusicImpl();
